import ReactECharts, { type EChartsOption } from "echarts-for-react";
import { useMemo } from "react";
import { useTriviaContext } from "../context-trivia";
import { getContainerScaledSize } from "../trivia-layout";
import { createRandomResults } from "./random-result-generator";

export function TriviaFeedbackChart() {
  const { config, currentQuestion, machine, analytics, containerSize } =
    useTriviaContext();

  const answers = useMemo(
    () => currentQuestion?.answers ?? [],
    [currentQuestion],
  );
  const selection = machine.selections.find(
    (s) => s.questionId === currentQuestion?.id,
  );

  const counts = useMemo(() => {
    const countByAnswer: Record<string, number> = {};

    if (currentQuestion) {
      for (const event of analytics) {
        const payload = event.payload?.[0];
        // The analytics payload only carries free-text prompt/label strings, not stable
        // question/answer ids, so historical events are matched here by case-folded text
        // equality. Editing a question's wording or an answer's label after it has been
        // played orphans its prior events silently — they will no longer match and will
        // simply stop contributing to this distribution. Fixing this needs a payload change
        // (stable ids) plus a backfill story for existing events; out of scope here.
        if (
          payload?.__typename === "EventPayloadTriviaQuestion" &&
          payload.answer &&
          payload.question?.trim().toLowerCase() ===
            currentQuestion.prompt.trim().toLowerCase()
        ) {
          const key = payload.answer.trim().toLowerCase();
          countByAnswer[key] = (countByAnswer[key] ?? 0) + 1;
        }
      }
    }

    if (selection?.answerId) {
      const chosen = answers.find((a) => a.id === selection.answerId);
      if (chosen) {
        const key = chosen.label.trim().toLowerCase();
        countByAnswer[key] = (countByAnswer[key] ?? 0) + 1;
      }
    }

    return answers.map((a) => countByAnswer[a.label.trim().toLowerCase()] ?? 0);
  }, [analytics, answers, currentQuestion, selection]);

  const hasRealData = counts.some((c) => c > 0);

  const results = useMemo<number[]>(() => {
    if (!hasRealData && !config.feedback.simulateDistribution) {
      return [];
    }

    const base = config.feedback.simulateDistribution
      ? createRandomResults(answers)
      : answers.map(() => 0);

    const combined = answers.map(
      (_, index) => (base[index] ?? 0) + counts[index],
    );

    // `combined` holds raw occurrence counts (or, when simulating, values that already sum to
    // ~100). The chart renders these against a fixed 0-100 axis and labels them with a "%"
    // suffix, so they must always be normalized to percentages here — simulated values already
    // sum to ~100, so this is a no-op for them within rounding.
    const total = combined.reduce((sum, value) => sum + value, 0);
    if (total <= 0) {
      return [];
    }

    return combined.map((value) => Math.round((value / total) * 100));
  }, [answers, config.feedback.simulateDistribution, counts, hasRealData]);

  const fontSize = getContainerScaledSize(containerSize, 0.045, 14);

  const option = useMemo<EChartsOption>(
    () => ({
      xAxis: [
        {
          type: "value",
          max: 100,
          axisLabel: { show: false },
          splitLine: { show: false },
        },
      ],
      yAxis: [
        {
          data: answers.map((answer) => answer.label),
          axisLabel: { show: false },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        },
      ],
      series: [
        {
          type: "bar",
          stack: "chart",
          barCategoryGap: 50,
          label: {
            position: [0, -fontSize * 1.4],
            formatter: "{b}",
            fontSize,
            color: config.theme.buttonText,
            show: true,
          },
          itemStyle: {
            borderRadius: [0, 20, 20, 0],
          },
          data: answers.map((answer, index) => ({
            value: results[index] ?? 0,
            itemStyle: {
              color: answer.correct
                ? config.theme.correctColor
                : config.theme.incorrectColor,
            },
          })),
        },
        {
          type: "bar",
          stack: "chart",
          barCategoryGap: 50,
          itemStyle: { color: "transparent" },
          label: {
            position: "insideRight",
            fontSize,
            color: config.theme.buttonText,
            formatter: (params: { value: number }) => `${params.value}%`,
            show: true,
          },
          data: results,
        },
      ],
    }),
    [
      answers,
      config.theme.buttonText,
      config.theme.correctColor,
      config.theme.incorrectColor,
      fontSize,
      results,
    ],
  );

  if (!currentQuestion || results.length === 0) {
    return null;
  }

  return (
    <div
      className="relative h-full w-full"
      style={{
        backgroundColor: config.theme.panelColor,
        borderRadius: "var(--trivia-radius)",
      }}
    >
      <ReactECharts
        option={option}
        opts={{ renderer: "canvas" }}
        style={{ height: "100%", width: "100%" }}
      />
    </div>
  );
}
